import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import type { TokenData } from '../types';

// Hook for fetching Solana network tokens from tokens_sol table
export const useSolanaTokens = (externalSearchQuery?: string) => {
    const [tokens, setTokens] = useState<TokenData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalTokens, setTotalTokens] = useState(0);
    const tokensPerPage = 20; // List view shows more per page

    const fetchTokens = async (page: number = 1) => {
        try {
            setLoading(true);
            setError(null);

            const effectiveQuery = externalSearchQuery !== undefined ? externalSearchQuery : searchQuery;

            console.log('🔍 [useSolanaTokens] Fetching tokens:', {
                page,
                effectiveQuery,
                externalSearchQuery,
                searchQuery
            });

            if (effectiveQuery && effectiveQuery.trim()) {
                // Search mode - no pagination
                const searchTerm = effectiveQuery.toLowerCase().trim();

                const { data, error: queryError } = await supabase
                    .from('tokens_sol')
                    .select('*')
                    .or(`name.ilike.%${searchTerm}%,symbol.ilike.%${searchTerm}%,mint_address.ilike.%${searchTerm}%,contract_address.ilike.%${searchTerm}%`)
                    .order('created_at', { ascending: false })
                    .limit(100);

                if (queryError) {
                    throw new Error(queryError.message);
                }

                setTokens(data || []);
                setTotalTokens((data || []).length);
                setTotalPages(1); // Search results don't use pagination
                setCurrentPage(1);
            } else {
                // Normal pagination mode

                // Get total count for pagination
                const { count, error: countError } = await supabase
                    .from('tokens_sol')
                    .select('*', { count: 'exact', head: true });

                if (countError) {
                    throw new Error(countError.message);
                }

                const totalCount = count || 0;
                const calculatedPages = Math.ceil(totalCount / tokensPerPage) || 1;

                setTotalTokens(totalCount);
                setTotalPages(calculatedPages);

                // Fetch paginated tokens
                const from = (page - 1) * tokensPerPage;
                const to = from + tokensPerPage - 1;

                const { data, error: queryError } = await supabase
                    .from('tokens_sol')
                    .select('*')
                    .order('created_at', { ascending: false })
                    .range(from, to);

                if (queryError) {
                    throw new Error(queryError.message);
                }

                console.log('🔍 [useSolanaTokens] Fetched tokens:', {
                    count: (data || []).length,
                    page,
                    totalPages: calculatedPages
                });

                setTokens(data || []);
                setCurrentPage(page);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch tokens');
        } finally {
            setLoading(false);
        }
    };

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            fetchTokens(page);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            goToPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            goToPage(currentPage - 1);
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        fetchTokens(1);
    };

    useEffect(() => {
        fetchTokens(1);
    }, []);

    // Trigger search when search query changes
    useEffect(() => {
        if (searchQuery !== '') {
            fetchTokens(1);
        }
    }, [searchQuery]);

    // Trigger search when external search query changes
    useEffect(() => {
        if (externalSearchQuery !== undefined) {
            fetchTokens(1);
        }
    }, [externalSearchQuery]);

    return {
        tokens,
        loading,
        error,
        searchQuery,
        setSearchQuery,
        refetch: () => fetchTokens(currentPage),
        clearSearch,
        // Pagination
        currentPage,
        totalPages,
        totalTokens,
        tokensPerPage,
        goToPage,
        goToNextPage,
        goToPreviousPage,
    };
};

